import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

/**
 * Hierarchical tree navigator. Flattens depth-first traversal — no inline
 * recursive sub-component (which trips babel's traversal limit).
 *
 * Props: nodes [{id, name, parent_id, order, path}], selectedId, onSelect, testIdPrefix
 */
function flatten(nodes, expanded) {
  const childrenOf = new Map();
  for (const n of nodes) {
    const p = n.parent_id || "__root__";
    if (!childrenOf.has(p)) childrenOf.set(p, []);
    childrenOf.get(p).push(n);
  }
  for (const arr of childrenOf.values()) {
    arr.sort((a, b) => (a.order || 0) - (b.order || 0) || a.name.localeCompare(b.name));
  }
  const out = [];
  const walk = (parentId, depth) => {
    const kids = childrenOf.get(parentId) || [];
    for (const n of kids) {
      const hasKids = childrenOf.has(n.id);
      out.push({ node: n, depth, hasKids });
      if (hasKids && expanded.has(n.id)) walk(n.id, depth + 1);
    }
  };
  walk("__root__", 0);
  return out;
}

export default function TreeNavigator({ nodes, selectedId, onSelect, testIdPrefix = "tree" }) {
  const [expanded, setExpanded] = useState(() => new Set());

  // Auto-expand ancestor chain of selected
  useEffect(() => {
    if (!selectedId) return;
    setExpanded((prev) => {
      const next = new Set(prev);
      let cur = nodes.find((n) => n.id === selectedId);
      while (cur && cur.parent_id) {
        next.add(cur.parent_id);
        cur = nodes.find((n) => n.id === cur.parent_id);
      }
      return next;
    });
  }, [selectedId, nodes]);

  const rows = useMemo(() => flatten(nodes, expanded), [nodes, expanded]);

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  return (
    <div className="space-y-0.5" data-testid={`${testIdPrefix}-root`}>
      <div
        onClick={() => onSelect(null)}
        className={`text-sm cursor-pointer rounded-sm py-1 px-1 hover:bg-slate-50 ${
          !selectedId ? "text-kb-primary font-medium" : "text-kb-ink-muted"
        }`}
        data-testid={`${testIdPrefix}-clear`}
      >
        All domains
      </div>
      {rows.map(({ node, depth, hasKids }) => {
        const isOpen = expanded.has(node.id);
        const isSelected = selectedId === node.id;
        return (
          <div
            key={node.id}
            style={{ paddingLeft: depth * 14 }}
            className={`flex items-center gap-1 py-1 text-sm cursor-pointer rounded-sm hover:bg-slate-50 transition-colors ${
              isSelected ? "text-kb-primary font-medium" : "text-kb-ink-muted"
            }`}
            data-testid={`${testIdPrefix}-node-${node.id}`}
          >
            <button
              onClick={(e) => { e.stopPropagation(); if (hasKids) toggle(node.id); }}
              className="w-4 h-4 inline-flex items-center justify-center shrink-0"
              aria-label={isOpen ? "Collapse" : "Expand"}
            >
              {!hasKids ? (
                <span className="w-1.5 h-1.5 bg-slate-300 rounded-full" />
              ) : isOpen ? (
                <ChevronDown className="w-3.5 h-3.5" />
              ) : (
                <ChevronRight className="w-3.5 h-3.5" />
              )}
            </button>
            <span onClick={() => onSelect(node)} className="flex-1 truncate hover:text-kb-primary">
              {node.name}
            </span>
          </div>
        );
      })}
    </div>
  );
}
